/**
 * ReferencesManager — gestor amplio de referencias (T-250).
 *
 * Reemplaza al panel flotante angosto de ReferencesUI por un Dialog tipo
 * gestor: tabla con búsqueda, filtros, orden, paginación y exportador.
 *
 * Props:
 *   open           — boolean (controla la apertura del Dialog).
 *   onClose        — () => void (se llama al cerrar el Dialog).
 *   documentId     — string (documento actual; usado como filtro por defecto).
 *   onGoToSource   — (row) => void (cierra el gestor, abre la tarjeta flotante
 *                    del documento y navega+resalta el párrafo citado).
 *
 * Backend (contratos del worker paralelo):
 *   GET    /references?document_id=&search=&sort=&order=&page=&page_size=
 *   GET    references export endpoint (format, document_id, search) → blob
 *   DELETE /documents/{documentId}/references/{id}
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Trash2, ChevronUp, ChevronDown, Download, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../lib/axios';
import ConfirmDialog from '../ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';

/** Mapa sort-key → etiqueta de columna ordenable. */
const SORTABLE = [
  { key: 'document', label: 'Documento' },
  { key: 'target_sheet', label: 'Hoja destino' },
  { key: 'source_sheet', label: 'Hoja origen' },
  { key: 'created_at', label: 'Fecha' },
];

/** Extensión de archivo por formato de exportación. */
const EXT_BY_FORMAT = {
  csv: 'csv',
  xlsx: 'xlsx',
  docx: 'docx',
  pdf: 'pdf',
  md: 'md',
  txt: 'txt',
};

const EXPORT_BUTTONS = [
  { format: 'csv', label: 'CSV' },
  { format: 'xlsx', label: 'Excel' },
  { format: 'docx', label: 'Word' },
  { format: 'pdf', label: 'PDF' },
  { format: 'md', label: 'MD' },
  { format: 'txt', label: 'TXT' },
];

// T-253: el detalle de una referencia vive DENTRO del gestor como columna del
// layout (no como overlay flotante): la tabla se encoge y ambos coexisten sin
// z-index ni pointer-events especiales. La tarjeta flotante es del documento.
// `onGoToSource(row)` lo resuelve el padre: cierra el gestor, abre la tarjeta
// flotante y navega+resalta.
const ReferencesManager = ({ open, onClose, documentId, onGoToSource }) => {
  const [detail, setDetail] = useState(null); // fila mostrada en el panel lateral
  // --- Estado de filtros / paginación / orden ----------------------------
  const [search, setSearch] = useState(''); // término crudo del input
  const [debouncedSearch, setDebouncedSearch] = useState(''); // término con debounce
  const [docFilter, setDocFilter] = useState(documentId || 'all'); // 'all' = todos
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // --- Estado de datos ---------------------------------------------------
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0); // bump para forzar refetch

  // --- Estado de exportación --------------------------------------------
  const [exporting, setExporting] = useState(false);

  // --- Estado de eliminación --------------------------------------------
  const [deleteRow, setDeleteRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // --- Debounce de búsqueda (300ms) -------------------------------------
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // --- Reset de página al cambiar filtros que invalidan el offset -------
  // (El select de documento resetea page a 1 — spec explícito.)
  const handleDocFilterChange = useCallback((val) => {
    setDocFilter(val);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((val) => {
    setPageSize(Number(val));
    setPage(1);
  }, []);

  // --- Fetch principal ---------------------------------------------------
  const loadRefs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        sort,
        order,
        page,
        page_size: pageSize,
      };
      // docFilter 'all' → omitir document_id (todos los documentos).
      const effDoc = docFilter === 'all' ? '' : docFilter;
      if (effDoc) params.document_id = effDoc;
      if (debouncedSearch) params.search = debouncedSearch;

      const r = await axiosInstance.get('/references', { params });
      setRows(r.data?.references || []);
      setTotal(r.data?.total ?? 0);
    } catch (e) {
      toast.error('No se pudieron cargar las referencias.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [sort, order, page, pageSize, docFilter, debouncedSearch]);

  useEffect(() => {
    if (open) loadRefs();
    // refreshTick fuerza recarga tras un delete sin añadir deps inestables.
    // T-250 (fix): sin directiva eslint — el plugin react-hooks NO está
    // registrado en este proyecto y CRA falla el build con "Definition for
    // rule not found" (anti-patrón ya documentado en _patrones.md, T-212).
  }, [open, sort, order, page, pageSize, docFilter, debouncedSearch, refreshTick, loadRefs]);

  // --- Orden: clic en encabezado alterna asc/desc ------------------------
  const handleSort = useCallback(
    (key) => {
      if (sort === key) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(key);
        setOrder('asc');
      }
    },
    [sort]
  );

  // --- Limpiar filtros ---------------------------------------------------
  const hasNonDefault =
    debouncedSearch !== '' ||
    docFilter !== (documentId || 'all') ||
    sort !== 'created_at' ||
    order !== 'desc' ||
    pageSize !== 25;

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setDebouncedSearch('');
    setDocFilter(documentId || 'all');
    setSort('created_at');
    setOrder('desc');
    setPageSize(25);
    setPage(1);
  }, [documentId]);

  // --- Exportación -------------------------------------------------------
  const handleExport = useCallback(
    async (format) => {
      setExporting(true);
      try {
        const params = { format };
        const effDoc = docFilter === 'all' ? '' : docFilter;
        if (effDoc) params.document_id = effDoc;
        if (debouncedSearch) params.search = debouncedSearch;

        const r = await axiosInstance.get('/references/export', {
          params,
          responseType: 'blob',
        });
        const ext = EXT_BY_FORMAT[format] || format;
        const fecha = new Date().toISOString().slice(0, 10);
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `referencias-${fecha}.${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        toast.error('No se pudo exportar');
      } finally {
        setExporting(false);
      }
    },
    [docFilter, debouncedSearch]
  );

  // --- Eliminar referencia ----------------------------------------------
  const handleDeleteClick = useCallback((row) => {
    setDeleteRow(row);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteRow) return;
    try {
      await axiosInstance.delete(
        `/documents/${deleteRow.document_id}/references/${deleteRow.id}`
      );
      toast.success('Referencia eliminada.');
      window.dispatchEvent(new CustomEvent('referencesUpdated'));
      setConfirmOpen(false);
      setDeleteRow(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'No se pudo eliminar la referencia.'
      );
    }
  }, [deleteRow]);

  // --- Métricas de paginación -------------------------------------------
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  // --- Render de indicador de orden -------------------------------------
  const sortIndicator = (key) => {
    if (sort !== key) return null;
    return order === 'asc' ? (
      <ChevronUp className="inline w-3 h-3 ml-0.5" />
    ) : (
      <ChevronDown className="inline w-3 h-3 ml-0.5" />
    );
  };

  const sortableKeys = useMemo(
    () => new Set(SORTABLE.map((s) => s.key)),
    []
  );

  const colHeaders = [
    { id: 'document', label: 'Documento' },
    { id: 'target_sheet', label: 'Hoja destino' },
    { id: 'target_anchor', label: 'Párrafo destino' },
    { id: 'source_sheet', label: 'Hoja origen' },
    { id: 'source_quote', label: 'Cita origen' },
    { id: 'reason', label: 'Motivo' },
    { id: 'created_at', label: 'Fecha' },
    { id: 'actions', label: '' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="references-manager"
        /* SIN `relative`: el DialogContent del repo se centra con
           `fixed left-[50%] top-[50%] translate-…`; añadir `relative` ganaba en
           el CSS compilado y el modal caía al flujo del documento (T-253). */
        className="glass border-0 max-w-6xl w-[95vw] max-h-[88vh] overflow-hidden flex flex-col"
      >
        {/* ---- Encabezado ---- */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Gestor de Referencias
            <span className="text-xs font-normal text-slate-400">
              {total} resultado{total === 1 ? '' : 's'}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* ---- Barra de herramientas ---- */}
        <div className="flex flex-wrap items-center gap-2 px-1">
          <input
            data-testid="refs-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar en cita, motivo o párrafo…"
            className="flex-1 min-w-[12rem] h-9 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring dark:text-slate-100 dark:border-slate-600"
          />

          <Select value={docFilter} onValueChange={handleDocFilterChange}>
            <SelectTrigger className="w-[12rem] h-9 text-xs dark:text-slate-100 dark:border-slate-600">
              <SelectValue placeholder="Documento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={documentId || 'this'}>Este documento</SelectItem>
              <SelectItem value="all">Todos los documentos</SelectItem>
            </SelectContent>
          </Select>

          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[7rem] h-9 text-xs dark:text-slate-100 dark:border-slate-600">
              <SelectValue placeholder="Tamaño" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>

          {hasNonDefault && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="h-9 px-3 rounded-md text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              Limpiar filtros
            </button>
          )}

          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">
              Exportar:
            </span>
            {EXPORT_BUTTONS.map((b) => (
              <button
                key={b.format}
                type="button"
                disabled={exporting}
                onClick={() => handleExport(b.format)}
                title={`Exportar ${b.label}`}
                className="h-9 px-2 rounded-md text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Download className="w-3 h-3" />
                {b.label}
              </button>
            ))}
          </div>
        </div>

        {/* ---- Tabla + panel de detalle (columnas hermanas, T-253) ---- */}
        <div className="flex-1 min-h-0 mt-2 flex gap-3">
        <div className="overflow-auto flex-1 min-w-0 rounded-md border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800">
              <tr className="text-left text-slate-500 dark:text-slate-400">
                {colHeaders.map((col) => {
                  const isSortable = sortableKeys.has(col.id);
                  return (
                    <th
                      key={col.id}
                      className={
                        'px-3 py-2 font-semibold whitespace-nowrap ' +
                        (isSortable
                          ? 'cursor-pointer select-none hover:text-slate-700 dark:hover:text-slate-200'
                          : '')
                      }
                      onClick={isSortable ? () => handleSort(col.id) : undefined}
                    >
                      {col.label}
                      {sortIndicator(col.id)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {loading && (
                <tr>
                  <td
                    colSpan={colHeaders.length}
                    className="px-3 py-6 text-center text-slate-400"
                  >
                    Cargando…
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && hasNonDefault && (
                <tr>
                  <td
                    colSpan={colHeaders.length}
                    className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                  >
                    Sin referencias que coincidan con la búsqueda.
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && !hasNonDefault && (
                <tr>
                  <td
                    colSpan={colHeaders.length}
                    className="px-3 py-6 text-center text-slate-500 dark:text-slate-400 leading-relaxed"
                  >
                    Aún no hay referencias. Actívalas en el chat (botón
                    Referencias).
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  >
                    <td
                      className="px-3 py-2 max-w-[16rem] truncate text-slate-700 dark:text-slate-200"
                      title={row.document_title || '—'}
                    >
                      {row.document_title || '—'}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[12rem] truncate text-slate-700 dark:text-slate-200"
                      title={row.target_sheet_name || '—'}
                    >
                      {row.target_sheet_name || '—'}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[16rem] truncate text-slate-700 dark:text-slate-200"
                      title={row.target_anchor_text || '—'}
                    >
                      {row.target_anchor_text || '—'}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[12rem] truncate text-slate-700 dark:text-slate-200"
                      title={row.source_sheet_name || '—'}
                    >
                      {row.source_sheet_name || '—'}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[16rem] truncate italic text-slate-600 dark:text-slate-300"
                      title={row.source_quote || '—'}
                    >
                      {row.source_quote || '—'}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[16rem] truncate text-slate-600 dark:text-slate-300"
                      title={row.reason || '—'}
                    >
                      {row.reason || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500 dark:text-slate-400">
                      {row.created_at
                        ? new Date(row.created_at).toLocaleDateString('es-ES')
                        : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          title="Ver detalle"
                          onClick={() => setDetail(row)}
                          className="p-1 rounded-md text-slate-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Eliminar"
                          onClick={() => handleDeleteClick(row)}
                          className="p-1 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* ---- Detalle de la referencia seleccionada ---- */}
        {detail && (
          <aside
            data-testid="reference-detail"
            className="w-80 shrink-0 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Detalle</h4>
              <button
                type="button"
                title="Cerrar detalle"
                onClick={() => setDetail(null)}
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Párrafo referenciado</p>
            <p className="text-xs text-slate-700 dark:text-slate-200 mb-2">{detail.target_anchor_text || '—'}</p>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Cita de la fuente</p>
            <blockquote className="italic text-xs text-slate-700 dark:text-slate-300 border-l-4 border-sky-400 pl-2 mb-2">
              {detail.source_quote || '—'}
            </blockquote>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Motivo</p>
            <p className="text-xs text-slate-700 dark:text-slate-200 mb-2">{detail.reason || '—'}</p>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">Origen</p>
            <p className="text-xs text-slate-700 dark:text-slate-200 mb-3">{detail.source_sheet_name || '—'}</p>
            <button
              type="button"
              onClick={() => onGoToSource?.(detail)}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-sky-600 text-white hover:bg-sky-700 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Ir a la fuente
            </button>
            <p className="mt-2 text-[10px] text-slate-400 dark:text-slate-500">
              Cierra el gestor y te lleva al párrafo citado, resaltado en el documento.
            </p>
          </aside>
        )}
        </div>

        {/* ---- Paginación ---- */}
        <div className="flex items-center justify-between gap-2 px-1 pt-2 text-xs text-slate-500 dark:text-slate-400">
          <span>
            Mostrando {rangeStart}–{rangeEnd} de {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span>
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </DialogContent>

      {/* ---- Confirmación de eliminación ---- */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Eliminar referencia"
        description="¿Seguro que deseas eliminar esta referencia? La acción no se puede deshacer."
        onConfirm={handleConfirmDelete}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
      />
    </Dialog>
  );
};

export default ReferencesManager;
